import { useEffect, useState } from "react";
import {
  BlueprintDetail,
  BlueprintRow,
  getBlueprintDetail,
  listBlueprints,
} from "@/api/methodologyConsole";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AdvancedJson,
  EmptyState,
  ErrorState,
  HashLabel,
  LifecycleBadge,
  LoadingState,
  ReasonCodes,
} from "./primitives";

const isDarkTest = (bp: Record<string, any>) =>
  bp?.planning_mode === "dark_test" ||
  bp?.status === "dark_test" ||
  Boolean(bp?.notes?.toLowerCase?.().includes("dark test"));

function Detail({ id }: { id: string }) {
  const [detail, setDetail] = useState<BlueprintDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDetail(null);
    getBlueprintDetail(id)
      .then(setDetail)
      .catch((e: Error) => setError(e.message));
  }, [id]);

  if (error) return <ErrorState message={error} />;
  if (!detail) return <LoadingState label="Loading blueprint…" />;

  const bp = detail.blueprint as Record<string, any>;
  const dark = isDarkTest(bp);
  const validationOk =
    detail.validation.length > 0 && detail.validation.every((c) => c.ok);
  const launchReady = Boolean(detail.launch_readiness?.ready);
  const approvable = !dark && validationOk && launchReady && bp.status !== "approved";

  const blockers: string[] = [];
  if (dark) blockers.push("dark_test_blueprint");
  if (!validationOk) blockers.push("blueprint_validation_failed");
  if (!launchReady)
    blockers.push(...(((detail.launch_readiness?.reason_codes as string[]) ?? []) || []));
  if (bp.status === "approved") blockers.push("already_approved");

  return (
    <div className="space-y-5">
      {dark && (
        <Alert>
          <AlertDescription className="font-semibold">
            DARK-TEST PLAN — this Blueprint exists for rehearsal only and can never be
            approved or executed.
          </AlertDescription>
        </Alert>
      )}

      <div className="text-sm">
        <div className="flex gap-3 border-b py-1.5">
          <span className="w-48 text-muted-foreground">Status</span>
          <LifecycleBadge status={bp.status} />
        </div>
        <div className="flex gap-3 border-b py-1.5">
          <span className="w-48 text-muted-foreground">Blueprint hash</span>
          <HashLabel value={bp.blueprint_hash} />
        </div>
        <div className="flex gap-3 border-b py-1.5">
          <span className="w-48 text-muted-foreground">Resolver</span>
          <span>
            {bp.resolver_function} {bp.resolver_version}
          </span>
        </div>
        <div className="flex gap-3 border-b py-1.5">
          <span className="w-48 text-muted-foreground">Questionnaire</span>
          <span>{bp.questionnaire_id ?? "—"}</span>
        </div>
        <div className="flex gap-3 border-b py-1.5">
          <span className="w-48 text-muted-foreground">Inventory</span>
          <span>
            {bp.module_count ?? 0} modules · {detail.question_count} questions ·{" "}
            {bp.estimated_minutes ?? "—"} minutes
          </span>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Validation</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.validation.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              The server returned no validation checks for this Blueprint.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {detail.validation.map((c) => (
                <li key={c.check_key} className="flex gap-2">
                  <span className={c.ok ? "text-success" : "text-destructive"}>
                    {c.ok ? "PASS" : "FAIL"}
                  </span>
                  <span className="font-medium">{c.check_key}</span>
                  <span className="text-muted-foreground">{c.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Client launch readiness</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">
            {launchReady
              ? "The server reports this Blueprint as launch-ready."
              : "The server reports this Blueprint as NOT launch-ready."}
          </p>
          <ReasonCodes codes={(detail.launch_readiness?.reason_codes as string[]) ?? []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Planned modules ({detail.modules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {detail.modules.length === 0 ? (
            <p className="text-sm text-muted-foreground">No module decisions recorded.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Decision</TableHead>
                  <TableHead>Reason code</TableHead>
                  <TableHead>Questions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.modules.map((m: any, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">
                      {m.decision ?? m.inclusion_decision ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.reason_code ?? m.decision_reason ?? "—"}
                    </TableCell>
                    <TableCell>{m.question_count ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="space-y-2">
        <Button disabled title={approvable ? undefined : "Approval is disabled"}>
          Approve Blueprint
        </Button>
        <p className="text-xs text-muted-foreground">
          Approval is intentionally unavailable in Phase 3.5A. Even when enabled, the server
          re-verifies validation, replay determinism and publication readiness.
        </p>
        <ReasonCodes codes={blockers} />
      </div>

      <details>
        <summary className="cursor-pointer text-sm text-muted-foreground">
          Advanced — raw Blueprint document (read-only)
        </summary>
        <div className="mt-2">
          <AdvancedJson data={bp.blueprint ?? bp} />
        </div>
      </details>
    </div>
  );
}

export default function BlueprintsTab() {
  const [rows, setRows] = useState<BlueprintRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    listBlueprints()
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!rows) return <LoadingState label="Loading blueprints…" />;

  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription className="text-sm">
          Opening this tab performs read-only queries. No Blueprint is planned, saved,
          approved or executed by viewing it. Preview and Save Draft remain deliberately
          unavailable until Phase 3.5B.
        </AlertDescription>
      </Alert>

      {rows.length === 0 ? (
        <EmptyState
          title="No blueprints"
          description="No Assessment Blueprint has been planned in this environment. Blueprints are produced by the server-side planner, never by this console."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Blueprint</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Questionnaire</TableHead>
              <TableHead>Modules</TableHead>
              <TableHead>Questions</TableHead>
              <TableHead>Hash</TableHead>
              <TableHead className="text-right">Detail</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">Version {r.blueprint_version}</TableCell>
                <TableCell>
                  <LifecycleBadge status={r.status} />
                </TableCell>
                <TableCell className="text-sm">{r.questionnaire_id ?? "—"}</TableCell>
                <TableCell>{r.module_count ?? 0}</TableCell>
                <TableCell>{r.question_count ?? 0}</TableCell>
                <TableCell>
                  <HashLabel value={r.blueprint_hash} />
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setOpenId(r.id)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Assessment Blueprint</SheetTitle>
            <SheetDescription>
              Read-only. Blueprint decisions and JSON cannot be edited from the browser.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">{openId && <Detail id={openId} />}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
